import React from 'react';
import { Button, Text } from '@amzn/storm-ui';
import styled from 'styled-components';
import {
  accountSelectionListId,
  CentricAccountDropdownSearchInput,
  CentricPaginationButton,
  containerWithBorderBottomMixin,
} from '../styles';
import { useCentricAccountDropdownSearch } from './useCentricAccountDropdownSearchAndFilter';
import { CentricAccountDropdownTypeSection } from './CentricAccountDropdownTypeSection';
import { CentricAccountDropdownInfoCard } from '../IA2AccountDropdownInfoCard';
import { CentricAccountDropdownFilterInput } from './CentricAccountDropdownFilterInput';
import { CentricAccountFilteredMessage } from './CentricAccountFilteredMessage';
import { FlexContainer } from 'src/commons/components/FlexContainer';
import { useAccountDropdownContext } from '../AccountDropdownProvider';
import { CentricAccountDropdownFooterLinks } from './CentricAccountDropdownFooterLinks';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getCentricAccountTypeListLabel } from '../AccountTypeLocalizationKeyMapping';
import { NoAccountsMessage } from '../NoAccountsMessage';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import {
  findSelectedCentricAccountSwitcherAccount,
  groupCentricAccounts,
} from '../accountHelper';
import { ErrorLoadingAccountsMessage } from 'src/features/header/components/AccountDropdown/ErrorLoadingAccountsMessage';

export const PAGE_SIZE = 30;

const AccountSwitcherContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.base}
    ${({ theme }) => theme.spacing.medium};
`;

const AccountListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: 0 ${({ theme }) => theme.spacing.medium};
  border-top: 1px solid ${({ theme }) => theme.palette.mercury};
  ${containerWithBorderBottomMixin}
`;

const AccountDropdownWithSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;

const AllAccountsMessageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.base};
  display: flex;
  justify-content: center;
`;
const SearchAndFilterContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.base};
`;

type CentricAccountDropdownProps = {
  advertisingAccountsDataTaktId?: string;
};

export const CentricAccountDropdown: React.VFC<CentricAccountDropdownProps> = ({
  advertisingAccountsDataTaktId,
}) => {
  const { centricAccounts, centricNextToken, currentEntityId, loadingStatus } =
    useAccountDropdownContext();

  const bundle = useAccountDropdownBundle();
  const chromeConfig = useChromeConfigContext();
  const isAllAccountsPageScope =
    chromeConfig.pageHeader?.pageScope === 'ALL_ACCOUNTS';
  const isFailedLoadingAccountData = loadingStatus === 'error';

  const {
    onFilter,
    setSearchInputRef,
    handleSearchChange,
    onSearchSubmit,
    onNextPage,
    searchValue,
    accounts,
    accountTypeFilterList,
    nextToken,
  } = useCentricAccountDropdownSearch({
    centricAccounts,
    centricNextToken,
  });

  const accountSearchPlaceholder = bundle.getMessage(
    'account-dropdown-placeholder',
  );

  if (isAllAccountsPageScope) {
    return (
      <AllAccountsMessageContainer>
        <Text>{bundle.getMessage('global-accounts-pill-message')}</Text>
      </AllAccountsMessageContainer>
    );
  }

  const selectedAccount = findSelectedCentricAccountSwitcherAccount(
    currentEntityId,
    accounts,
  );

  const groupedAccounts = groupCentricAccounts(accounts);

  return (
    <AccountDropdownWithSearchContainer>
      <AccountSwitcherContainer>
        <CentricAccountDropdownInfoCard />
        <SearchAndFilterContainer>
          <FlexContainer>
            <CentricAccountDropdownSearchInput
              data-takt-id="ccx-centric-account-switcher-search"
              id="centric-account-switcher-search"
              placeholder={accountSearchPlaceholder}
              inputRef={setSearchInputRef}
              value={searchValue}
              onChange={handleSearchChange}
              onClear={handleSearchChange}
              fullWidth={true}
              label={accountSearchPlaceholder}
            />
            <Button onClick={onSearchSubmit} type="submit">
              {bundle.getMessage('account-dropdown-search-button') ?? 'Submit'}
            </Button>
          </FlexContainer>
          <FlexContainer justifyContent="end">
            <CentricAccountDropdownFilterInput
              accountTypes={accountTypeFilterList}
              onSubmit={onFilter}
            />
          </FlexContainer>
          {accountTypeFilterList.some((filter) => !filter.isSelected) && (
            <CentricAccountFilteredMessage numAccounts={accounts.length} />
          )}
        </SearchAndFilterContainer>
      </AccountSwitcherContainer>
      <AccountListContainer
        id={accountSelectionListId}
        data-takt-id={advertisingAccountsDataTaktId}
      >
        {isFailedLoadingAccountData && <ErrorLoadingAccountsMessage />}
        {!isFailedLoadingAccountData && accounts.length === 0 && (
          <NoAccountsMessage />
        )}
        {!isFailedLoadingAccountData &&
          groupedAccounts.length > 0 &&
          groupedAccounts.map(
            (accountTypeItem) =>
              accountTypeItem.accounts.length > 0 && (
                <CentricAccountDropdownTypeSection
                  key={accountTypeItem.label}
                  accounts={accountTypeItem.accounts}
                  label={getCentricAccountTypeListLabel(
                    accountTypeItem.accountType,
                    bundle,
                  )}
                  type={accountTypeItem.accountType}
                  selectedEntityId={selectedAccount?.accountId}
                />
              ),
          )}
        {nextToken && (
          <CentricPaginationButton onClick={onNextPage}>
            {bundle.formatMessage(
              'account-dropdown-invalid-account-type-pagination',
              {
                pageSize: PAGE_SIZE.toString(),
              },
            )}
          </CentricPaginationButton>
        )}
      </AccountListContainer>
      <CentricAccountDropdownFooterLinks />
    </AccountDropdownWithSearchContainer>
  );
};
